import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import api from "../services/api";
import "../styles/Homepage.css";

/* =========================================================
   SCROLL REVEAL HOOK
   ========================================================= */
function useScrollReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      {
        threshold: options.threshold || 0.12,
        rootMargin: options.rootMargin || "0px 0px -60px 0px",
      },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return [ref, visible];
}

/* =========================================================
   REVEAL WRAPPER COMPONENT
   ========================================================= */
function Reveal({
  children,
  dir = "up",
  delay = 0,
  dist = 48,
  className = "",
}) {
  const [ref, visible] = useScrollReveal();

  const transforms = {
    up: `translateY(${dist}px)`,
    down: `translateY(-${dist}px)`,
    left: `translateX(${dist}px)`,
    right: `translateX(-${dist}px)`,
    scale: `scale(0.94)`,
    none: "none",
  };

  return (
    <div
      ref={ref}
      className={`hp-reveal ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : transforms[dir],
        transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
        willChange: "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}

/* =========================================================
   HERO SLIDER
   ========================================================= */
const SLIDES = [
  {
    eyebrow: "New Season",
    title: "Heritage\nLungies",
    sub: "Handwoven in Tamil Nadu — craft in every thread",
    cta: "Shop the Collection",
    category: "Lungies",
    slug: "premium-printed-lungies",
    img: "/assets/1.png",
    accent: "#b8935a",
  },
  {
    eyebrow: "Pure Cotton",
    title: "Checked\nLungies",
    sub: "Classic checks reimagined for the modern man",
    cta: "Explore Now",
    category: "Lungies",
    slug: "checked-lungies",
    img: "/assets/2.png",
    accent: "#7a6248",
  },
  {
    eyebrow: "Handloom Tradition",
    title: "Chennimalai\nBedsheets",
    sub: "Authentic weaves that carry a century of stories",
    cta: "Shop Bedsheets",
    category: "Bedsheets",
    slug: "chennimalai-bedsheets",
    img: "/assets/3.png",
    accent: "#9e8060",
  },
  {
    eyebrow: "Signature Collection",
    title: "Kaavi\nWeaves",
    sub: "Ochre-dyed, earth-born — rooted in ritual",
    cta: "Discover Kaavi",
    category: "Kaavi",
    slug: "kaavi-collection",
    img: "/assets/4.png",
    accent: "#c46a2a",
  },
  {
    eyebrow: "Luxury Bath",
    title: "Premium\nTowels",
    sub: "Soft loops. Pure cotton. Enduring quality.",
    cta: "Shop Towels",
    category: "Towel",
    slug: "towel-collection",
    img: "/assets/5.png",
    accent: "#6b8c7a",
  },
];

function HeroSlider() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [prev, setPrev] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const intervalRef = useRef(null);

  const goTo = useCallback(
    (idx) => {
      if (transitioning || idx === active) return;
      setTransitioning(true);
      setPrev(active);
      setActive(idx);
      setTimeout(() => {
        setPrev(null);
        setTransitioning(false);
      }, 1100);
    },
    [active, transitioning],
  );

  const next = useCallback(
    () => goTo((active + 1) % SLIDES.length),
    [active, goTo],
  );
  const prev_ = useCallback(
    () => goTo((active - 1 + SLIDES.length) % SLIDES.length),
    [active, goTo],
  );

  useEffect(() => {
    intervalRef.current = setInterval(next, 7000);
    return () => clearInterval(intervalRef.current);
  }, [next]);

  const slide = SLIDES[active];

  return (
    <section className="hp-hero" aria-label="Hero slider">
      {/* Slides */}
      {SLIDES.map((s, i) => (
        <div
          key={i}
          className={`hp-hero-slide ${i === active ? "is-active" : ""} ${i === prev ? "is-prev" : ""}`}
          aria-hidden={i !== active}
        >
          <div className="hp-hero-img-wrap">
            <img
              src={s.img}
              alt={s.title.replace("\n", " ")}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
          <div className="hp-hero-veil" />
        </div>
      ))}

      {/* Content */}
      <div className="hp-hero-content" key={active}>
        <div className="hp-hero-inner">
          <span className="hp-hero-eyebrow">{slide.eyebrow}</span>
          <h1 className="hp-hero-title" style={{ whiteSpace: "pre-line" }}>
            {slide.title}
          </h1>
          <p className="hp-hero-sub">{slide.sub}</p>
          <div className="hp-hero-actions">
            <button
              className="hp-hero-btn hp-hero-btn--primary"
              onClick={() =>
                navigate("/collection", {
                  state: { category: slide.category, slug: slide.slug },
                })
              }
              style={{ "--accent": slide.accent }}
            >
              {slide.cta}
            </button>
            <Link to="/collection" className="hp-hero-btn hp-hero-btn--ghost">
              All Collections
            </Link>
          </div>
        </div>
      </div>

      {/* Side indicators */}
      <div className="hp-hero-index">
        <span className="hp-hero-index-current">0{active + 1}</span>
        <div className="hp-hero-index-bar">
          <div
            className="hp-hero-index-fill"
            style={{ height: `${((active + 1) / SLIDES.length) * 100}%` }}
          />
        </div>
        <span className="hp-hero-index-total">0{SLIDES.length}</span>
      </div>

      {/* Arrow controls */}
      <div className="hp-hero-arrows">
        <button
          className="hp-hero-arrow"
          onClick={prev_}
          aria-label="Previous slide"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <button
          className="hp-hero-arrow"
          onClick={next}
          aria-label="Next slide"
        >
          <svg
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Dots */}
      <div className="hp-hero-dots" role="tablist">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            className={`hp-hero-dot ${i === active ? "is-active" : ""}`}
            onClick={() => goTo(i)}
            role="tab"
            aria-selected={i === active}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="hp-hero-scroll" aria-hidden="true">
        <div className="hp-hero-scroll-mouse">
          <div className="hp-hero-scroll-dot" />
        </div>
        <span>Scroll</span>
      </div>
    </section>
  );
}

/* =========================================================
   TICKER STRIP
   ========================================================= */
function Ticker() {
  const items = [
    "Free Shipping on orders above ₹1,000",
    "100% Pure Cotton Guaranteed",
    "7-Day Easy Returns",
    "Handwoven in Tamil Nadu",
    "Kaavi Dyed Heritage Textiles",
    "New Season Now In",
  ];
  const full = [...items, ...items, ...items];
  return (
    <div className="hp-ticker" aria-hidden="true">
      <div className="hp-ticker-track">
        {full.map((item, i) => (
          <span key={i} className="hp-ticker-item">
            {item}
            <span className="hp-ticker-sep">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   TRUST BAR
   ========================================================= */
const TRUST = [
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        width="22"
        height="22"
      >
        <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" />
        <path
          d="M3 17V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10"
          strokeLinecap="round"
        />
      </svg>
    ),
    label: "Free Shipping",
    sub: "Orders above ₹1,000",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        width="22"
        height="22"
      >
        <polyline points="1 4 1 10 7 10" />
        <path d="M3.51 15a9 9 0 1 0 .49-4.5" strokeLinecap="round" />
      </svg>
    ),
    label: "Easy Returns",
    sub: "7-day return policy",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        width="22"
        height="22"
      >
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" strokeLinecap="round" />
      </svg>
    ),
    label: "Secure Payment",
    sub: "100% safe checkout",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        width="22"
        height="22"
      >
        <path
          d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
          strokeLinecap="round"
        />
      </svg>
    ),
    label: "Authentic Weave",
    sub: "Heritage certified",
  },
];

function TrustBar() {
  return (
    <div className="hp-trust">
      <div className="hp-container">
        {TRUST.map((t, i) => (
          <Reveal key={t.label} dir="up" delay={i * 80}>
            <div className="hp-trust-item">
              <span className="hp-trust-icon">{t.icon}</span>
              <div className="hp-trust-text">
                <strong>{t.label}</strong>
                <span>{t.sub}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

/* =========================================================
   SECTION HEADER
   ========================================================= */
function SectionHeader({ eyebrow, title, sub, to, state, align = "left" }) {
  return (
    <div className={`hp-sec-header hp-sec-header--${align}`}>
      <Reveal dir="up">
        {eyebrow && <span className="hp-sec-eyebrow">{eyebrow}</span>}
        <h2 className="hp-sec-title">{title}</h2>
        {sub && <p className="hp-sec-sub">{sub}</p>}
      </Reveal>
      {to && (
        <Reveal dir="up" delay={100}>
          <Link to={to} state={state} className="hp-sec-link">
            View all
            <svg
              viewBox="0 0 24 24"
              width="13"
              height="13"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </Reveal>
      )}
    </div>
  );
}

/* =========================================================
   FEATURED CATEGORIES
   ========================================================= */
const CATEGORIES = [
  {
    label: "Lungies",
    eyebrow: "Best Seller",
    img: "/assets/lung.png",
    category: "Lungies",
    slug: "premium-printed-lungies",
    size: "tall",
  },
  {
    label: "Bedsheets",
    eyebrow: "Home Comfort",
    img: "/assets/bedsh.png",
    category: "Bedsheets",
    slug: "bedsheets",
    size: "normal",
  },
  {
    label: "Kaavi",
    eyebrow: "Traditional",
    img: "/assets/kaav.png",
    category: "Kaavi",
    slug: "kaavi-collection",
    size: "normal",
  },
  {
    label: "Towels",
    eyebrow: "Luxury Bath",
    img: "/assets/towe.png",
    category: "Towel",
    slug: "towel-collection",
    size: "normal",
  },
  {
    label: "Hometex",
    eyebrow: "New In",
    img: "/assets/towe.png",
    category: "Hometex",
    slug: "hometex-collection",
    size: "normal",
  },
];

function FeaturedCategories() {
  const navigate = useNavigate();
  return (
    <section className="hp-section hp-categories-section">
      <div className="hp-container">
        <SectionHeader
          eyebrow="Shop by Category"
          title="Explore the Collection"
          to="/collection"
        />
        <div className="hp-cat-grid">
          {CATEGORIES.map((c, i) => (
            <Reveal
              key={c.label}
              dir="up"
              delay={i * 70}
              className={`hp-cat-cell ${c.size === "tall" ? "hp-cat-cell--tall" : ""}`}
            >
              <button
                className="hp-cat-card"
                onClick={() =>
                  navigate("/collection", {
                    state: { category: c.category, slug: c.slug },
                  })
                }
                aria-label={`Shop ${c.label}`}
              >
                <div className="hp-cat-img">
                  <img src={c.img} alt={c.label} loading="lazy" />
                </div>
                <div className="hp-cat-veil" />
                <div className="hp-cat-body">
                  <span className="hp-cat-eyebrow">{c.eyebrow}</span>
                  <span className="hp-cat-label">{c.label}</span>
                  <span className="hp-cat-cta">
                    Shop
                    <svg
                      viewBox="0 0 24 24"
                      width="11"
                      height="11"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </span>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   PRODUCT ROW (with loading skeleton)
   ========================================================= */
function ProductRow({ eyebrow, title, sub, category, slug, limit = 4 }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.products
      .getAll({ category, subcategory: slug, limit, sort: "featured" })
      .then((d) => setProducts(d.products || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, slug, limit]);

  if (!loading && products.length === 0) return null;

  return (
    <section className="hp-section">
      <div className="hp-container">
        <SectionHeader
          eyebrow={eyebrow}
          title={title}
          sub={sub}
          to="/collection"
          state={{ category, slug }}
        />
        <div className="hp-prod-grid">
          {loading
            ? [...Array(limit)].map((_, i) => (
                <div key={i} className="hp-prod-skeleton" style={{ "--i": i }}>
                  <div className="hp-prod-skeleton-img" />
                  <div
                    className="hp-prod-skeleton-line"
                    style={{ width: "72%" }}
                  />
                  <div
                    className="hp-prod-skeleton-line"
                    style={{ width: "44%" }}
                  />
                </div>
              ))
            : products.map((p, i) => (
                <Reveal key={p._id} dir="up" delay={i * 80}>
                  <ProductCard product={p} />
                </Reveal>
              ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   FULL-WIDTH EDITORIAL BANNER
   ========================================================= */
function EditorialBanner({
  eyebrow,
  title,
  sub,
  cta,
  img,
  category,
  slug,
  flip = false,
}) {
  const navigate = useNavigate();
  const [ref, visible] = useScrollReveal({ threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`hp-editorial ${flip ? "hp-editorial--flip" : ""} ${visible ? "is-visible" : ""}`}
      onClick={() => navigate("/collection", { state: { category, slug } })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) =>
        e.key === "Enter" &&
        navigate("/collection", { state: { category, slug } })
      }
      aria-label={`Shop ${title}`}
    >
      <div className="hp-editorial-img">
        <img src={img} alt={title} loading="lazy" />
        <div className="hp-editorial-img-veil" />
      </div>
      <div className="hp-editorial-body">
        <div className="hp-editorial-body-inner">
          {eyebrow && <span className="hp-editorial-eyebrow">{eyebrow}</span>}
          <h2 className="hp-editorial-title">{title}</h2>
          {sub && <p className="hp-editorial-sub">{sub}</p>}
          <span className="hp-editorial-cta">
            {cta}
            <svg
              viewBox="0 0 24 24"
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   PROMO BANNER (full-bleed, dark overlay)
   ========================================================= */
function PromoBanner({
  tag,
  headline,
  sub,
  cta,
  img,
  badgeText,
  category,
  slug,
}) {
  const navigate = useNavigate();
  const [ref, visible] = useScrollReveal({ threshold: 0.08 });
  return (
    <div ref={ref} className={`hp-promo ${visible ? "is-visible" : ""}`}>
      <img src={img} alt={headline} className="hp-promo-bg" loading="lazy" />
      <div className="hp-promo-veil" />
      {badgeText && <div className="hp-promo-badge">{badgeText}</div>}
      <div className="hp-promo-content">
        {tag && <span className="hp-promo-tag">{tag}</span>}
        <h2 className="hp-promo-headline">{headline}</h2>
        {sub && <p className="hp-promo-sub">{sub}</p>}
        <button
          className="hp-promo-btn"
          onClick={() => navigate("/collection", { state: { category, slug } })}
        >
          {cta}
        </button>
      </div>
    </div>
  );
}

/* =========================================================
   WHY KKB — Feature grid
   ========================================================= */
const WHY = [
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="28"
        height="28"
      >
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    title: "Traditional Weave",
    desc: "Each piece is handcrafted using weaving techniques passed through generations of Tamil Nadu artisans.",
    note: "100% Handloom",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="28"
        height="28"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    title: "Premium Cotton",
    desc: "Sourced from the finest cotton farms, woven into fabric with exceptional durability and breathability.",
    note: "Premium Grade",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="28"
        height="28"
      >
        <circle cx="12" cy="12" r="10" />
        <path
          d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32"
          strokeLinecap="round"
        />
      </svg>
    ),
    title: "Natural Dyes",
    desc: "Our Kaavi collection uses ancient natural dyeing techniques — earth pigments that age beautifully.",
    note: "Eco-Conscious",
  },
  {
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        width="28"
        height="28"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
      </svg>
    ),
    title: "Lifetime Quality",
    desc: "Textiles built to last — proper care means these pieces become heirlooms, not fast fashion.",
    note: "Enduring Craft",
  },
];

function WhyKKB() {
  return (
    <section className="hp-section hp-why-section">
      <div className="hp-container">
        <SectionHeader
          eyebrow="Our Promise"
          title="Why KKB & Co."
          align="center"
        />
        <div className="hp-why-grid">
          {WHY.map((w, i) => (
            <Reveal key={w.title} dir="up" delay={i * 100}>
              <div className="hp-why-card">
                <div className="hp-why-icon">{w.icon}</div>
                <h3 className="hp-why-title">{w.title}</h3>
                <p className="hp-why-desc">{w.desc}</p>
                <span className="hp-why-note">{w.note}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   TESTIMONIALS
   ========================================================= */
const REVIEWS = [
  {
    name: "Rajesh M.",
    location: "Chennai",
    rating: 5,
    text: "The quality is unlike anything I've found online. The weave is tight, the colours don't bleed. My father wore a KKB lungi for 15 years — now I understand why.",
    tag: "Verified Buyer",
  },
  {
    name: "Priya S.",
    location: "Bangalore",
    rating: 5,
    text: "Ordered the Chennimalai bedsheets and they're absolutely gorgeous. The fabric gets softer after every wash. Will be gifting these this Diwali.",
    tag: "Repeat Customer",
  },
  {
    name: "Kumar T.",
    location: "Coimbatore",
    rating: 5,
    text: "The Kaavi collection is stunning. You can feel the tradition in the texture. Fast delivery, beautiful packaging, and the quality exceeded expectations.",
    tag: "Verified Buyer",
  },
  {
    name: "Meena R.",
    location: "Madurai",
    rating: 4,
    text: "Very happy with the towels — thick, absorbent, zero lint. Customer service was helpful when I had a sizing query. Recommended to all my friends.",
    tag: "Verified Buyer",
  },
];

function StarIcon({ filled }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function Testimonials() {
  const trackRef = useRef(null);
  const [pos, setPos] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const startPos = useRef(0);

  const clamp = (v) => Math.min(0, Math.max(v, -(REVIEWS.length - 1) * 340));

  const onMouseDown = (e) => {
    setDragging(true);
    startX.current = e.clientX;
    startPos.current = pos;
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    setPos(clamp(startPos.current + (e.clientX - startX.current)));
  };
  const onMouseUp = () => setDragging(false);
  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
    startPos.current = pos;
  };
  const onTouchMove = (e) =>
    setPos(clamp(startPos.current + (e.touches[0].clientX - startX.current)));

  return (
    <section className="hp-section hp-reviews-section">
      <div className="hp-container">
        <SectionHeader
          eyebrow="From Our Customers"
          title="What they're saying"
          align="center"
        />
      </div>
      <div
        className="hp-reviews-track-wrap"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
      >
        <div
          ref={trackRef}
          className="hp-reviews-track"
          style={{
            transform: `translateX(calc(${pos}px + 48px))`,
            cursor: dragging ? "grabbing" : "grab",
            transition: dragging
              ? "none"
              : "transform 0.4s cubic-bezier(0.16,1,0.3,1)",
          }}
        >
          {REVIEWS.map((r, i) => (
            <Reveal key={i} dir="up" delay={i * 80}>
              <article className="hp-review-card">
                <header className="hp-review-head">
                  <div className="hp-review-avatar">{r.name.charAt(0)}</div>
                  <div>
                    <p className="hp-review-name">{r.name}</p>
                    <p className="hp-review-loc">{r.location}</p>
                  </div>
                  <span className="hp-review-badge">{r.tag}</span>
                </header>
                <div className="hp-review-stars">
                  {[...Array(5)].map((_, s) => (
                    <span
                      key={s}
                      className={s < r.rating ? "hp-star--on" : "hp-star--off"}
                    >
                      <StarIcon filled={s < r.rating} />
                    </span>
                  ))}
                </div>
                <p className="hp-review-text">&ldquo;{r.text}&rdquo;</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =========================================================
   NEWSLETTER
   ========================================================= */
function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return;
    setStatus("sending");
    await new Promise((r) => setTimeout(r, 1200));
    setStatus("done");
  };

  return (
    <section className="hp-newsletter">
      <div className="hp-newsletter-inner">
        <Reveal dir="up">
          <span className="hp-newsletter-eyebrow">Join the Community</span>
          <h2 className="hp-newsletter-title">
            Craft stories, seasonal drops,
            <br />
            and exclusive offers.
          </h2>
          <p className="hp-newsletter-sub">
            No spam, ever. Unsubscribe any time.
          </p>
        </Reveal>
        <Reveal dir="up" delay={120}>
          {status === "done" ? (
            <div className="hp-newsletter-success">
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              You're on the list — welcome to the family.
            </div>
          ) : (
            <form className="hp-newsletter-form" onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="hp-newsletter-input"
                required
                aria-label="Email address"
              />
              <button
                type="submit"
                className="hp-newsletter-btn"
                disabled={status === "sending"}
              >
                {status === "sending" ? (
                  <span className="hp-newsletter-spinner" />
                ) : (
                  <>
                    Subscribe
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* =========================================================
   FOOTER
   ========================================================= */
function Footer() {
  return (
    <footer className="hp-footer">
      <div className="hp-footer-top">
        <div className="hp-footer-brand">
          <Link to="/" className="hp-footer-logo">
            <img src="/assets/kkblogo.png" alt="KKB & Co." />
            <span>KKB</span>
          </Link>
          <p className="hp-footer-brand-desc">
            Heritage textiles from Tamil Nadu — handwoven by artisans who have
            practised this craft for generations.
          </p>
          <div className="hp-footer-socials">
            {[
              {
                label: "Instagram",
                href: "https://www.instagram.com/kkbclothing_official",
                icon: (
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="currentColor"
                  >
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                ),
              },
              {
                label: "Facebook",
                href: "https://www.facebook.com/ClothingfromKKBstores",
                icon: (
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="currentColor"
                  >
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                ),
              },
              {
                label: "YouTube",
                href: "https://www.youtube.com/@kkbkailiking6440",
                icon: (
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="currentColor"
                  >
                    <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z" />
                  </svg>
                ),
              },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                className="hp-footer-social"
                aria-label={s.label}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        <nav className="hp-footer-nav" aria-label="Footer navigation">
          <div className="hp-footer-col">
            <h4>Shop</h4>
            <ul>
              {[
                ["Lungies", "/collection", { category: "Lungies" }],
                ["Bedsheets", "/collection", { category: "Bedsheets" }],
                ["Kaavi", "/collection", { category: "Kaavi" }],
                ["Towels", "/collection", { category: "Towel" }],
                ["New Arrivals", "/collection", {}],
              ].map(([label, to, state]) => (
                <li key={label}>
                  <Link to={to} state={state}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="hp-footer-col">
            <h4>Company</h4>
            <ul>
              {[
                ["Our Story", "/about"],
                ["Contact Us", "/contact"],
                ["Track Order", "/track"],
                ["Careers", "/careers"],
              ].map(([label, to]) => (
                <li key={label}>
                  <Link to={to}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="hp-footer-col">
            <h4>Support</h4>
            <ul>
              {[
                ["FAQs", "/faqs"],
                ["Shipping Policy", "/shipping"],
                ["Return Policy", "/returns"],
                ["Privacy Policy", "/privacy"],
                ["Terms of Service", "/terms"],
              ].map(([label, to]) => (
                <li key={label}>
                  <Link to={to}>{label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="hp-footer-col">
            <h4>Contact</h4>
            <address className="hp-footer-contact">
              <p>KKB & Co. Textiles</p>
              <p>Chennimalai, Tamil Nadu</p>
              <a href="mailto:hello@kkbco.in">hello@kkbco.in</a>
              <a href="tel:+919876543210">+91 98765 43210</a>
            </address>
          </div>
        </nav>
      </div>

      <div className="hp-footer-bottom">
        <p>
          © {new Date().getFullYear()} KKB & Co. All rights reserved.
          Handcrafted with care in Tamil Nadu.
        </p>
        <div className="hp-footer-pay-icons">
          {["Visa", "Mastercard", "UPI", "RazorPay"].map((p) => (
            <span key={p} className="hp-footer-pay-badge">
              {p}
            </span>
          ))}
        </div>
      </div>
    </footer>
  );
}

/* =========================================================
   FLOATING CART BUTTON
   ========================================================= */
function FloatingCart() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <Link
      to="/cart"
      className={`hp-float-cart ${scrolled ? "hp-float-cart--visible" : ""}`}
      aria-label="View cart"
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
      <span>Cart</span>
    </Link>
  );
}

/* =========================================================
   HOME PAGE
   ========================================================= */
export default function HomePage() {
  return (
    <div className="hp-page">
      {/* Grain texture overlay */}
      <div className="hp-grain" aria-hidden="true" />

      <HeroSlider />
      <Ticker />
      <TrustBar />

      <FeaturedCategories />

      <ProductRow
        eyebrow="Just Arrived"
        title="New Arrivals"
        sub="Fresh weaves for the new season"
        category="Lungies"
        slug="premium-printed-lungies"
        limit={4}
      />

      <EditorialBanner
        eyebrow="The Handloom Story"
        title="Chennimalai Bedsheets"
        sub="Authentic handloom tradition from the heart of Tamil Nadu — every thread a story."
        cta="Shop the Collection"
        img="/assets/bedsh.png"
        category="Bedsheets"
        slug="chennimalai-bedsheets"
      />

      <ProductRow
        eyebrow="Heritage Weave"
        title="Checked Lungies"
        sub="Classic checks, endlessly wearable"
        category="Lungies"
        slug="checked-lungies"
        limit={4}
      />

      <PromoBanner
        tag="Limited Edition"
        headline="Up to 40% Off — Season Sale"
        sub="Our biggest sale of the year. Pure cotton, deep discounts, heritage quality."
        cta="Shop the Sale"
        img="/assets/kaav.png"
        badgeText="SALE"
        category="Lungies"
        slug="premium-printed-lungies"
      />

      <ProductRow
        eyebrow="Home Comfort"
        title="Bedsheets Collection"
        category="Bedsheets"
        slug="bedsheets"
        limit={4}
      />

      <EditorialBanner
        eyebrow="Earth-Dyed · Hand-Loomed"
        title="Kaavi Collection"
        sub="Ochre pigments, ritual patterns — rooted in south Indian tradition."
        cta="Explore Kaavi"
        img="/assets/kaav.png"
        category="Kaavi"
        slug="kaavi-collection"
        flip
      />

      <ProductRow
        eyebrow="Luxury Bath"
        title="Towel Collection"
        sub="Soft. Absorbent. Enduring."
        category="Towel"
        slug="towel-collection"
        limit={4}
      />

      <WhyKKB />
      <Testimonials />
      <Newsletter />
      {/* <Footer /> */}
      <FloatingCart />
    </div>
  );
}
